import React, { useState, useMemo } from "react";
import * as adminService from "@/shared/api/services/adminService";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import { createPageUrl } from "@/shared/utils";
import { useLanguage } from "@/shared/components/shared/LanguageContext";
import { Button } from "@/shared/components/ui/button";
import { Input } from "@/shared/components/ui/input";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/shared/components/ui/alert-dialog";
import { Plus, Search } from "lucide-react";
import EntityPanel from "@/shared/components/shared/EntityPanel";

export default function AdminResellers() {
  const [search, setSearch] = useState("");
  const [deleteDialog, setDeleteDialog] = useState(null);
  const [statusDialog, setStatusDialog] = useState(null);
  const { t } = useLanguage();
  const queryClient = useQueryClient();

  const { data: resellers = [], isLoading } = useQuery({
    queryKey: ["admin-resellers"],
    queryFn: () => adminService.getAllResellers(),
  });

  const { data: engines = [] } = useQuery({
    queryKey: ["engines"],
    queryFn: () => adminService.getAllEngines(),
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }) => adminService.updateReseller(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries(["admin-resellers"]);
      setStatusDialog(null);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id) => adminService.deleteReseller(id),
    onSuccess: () => {
      queryClient.invalidateQueries(["admin-resellers"]);
      setDeleteDialog(null);
    },
  });

  const filteredResellers = resellers.filter(r =>
    r.name?.toLowerCase().includes(search.toLowerCase()) ||
    r.email?.toLowerCase().includes(search.toLowerCase())
  );



  const getMonthlyVolume = (resellerId) => {
    // Mock calculation
    return Math.floor(Math.random() * 10000000) + 500000;
  };

  const handleToggleStatus = async () => {
    if (!statusDialog) return;
    const newStatus = statusDialog.status === "ACTIVE" ? "SUSPENDED" : "ACTIVE";
    await updateMutation.mutateAsync({
      id: statusDialog.id,
      data: { name: statusDialog.name, status: newStatus },
    });
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">{t("resellers")}</h1>
          <p className="text-slate-500">
            {isLoading ? (
              <Skeleton width={150} />
            ) : (
              `${resellers.length} ${t("resellerPartners")}`
            )}
          </p>
        </div>
        <Link to={createPageUrl("AdminResellerForm")}>
          <Button className="gap-2 bg-blue-600 hover:bg-blue-700 hover:scale-105 transition-all duration-300 shadow-sm hover:bg-gradient-to-br hover:from-blue-500 hover:to-blue-700">
            <Plus className="h-4 w-4" />
            {t("createReseller")}
          </Button>
        </Link>
      </div>

      {/* Search */}
      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
        <Input
          placeholder={t("searchResellers")}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="pl-10"
        />
      </div>

      {/* Table */}
      <EntityPanel
        entityType="reseller"
        data={filteredResellers}
        isLoading={isLoading}
        t={t}
        engines={engines}
        getMonthlyVolume={getMonthlyVolume}
        onStatusClick={setStatusDialog}
        onDeleteClick={setDeleteDialog}
      />

      {/* Status Toggle Dialog */}
      <AlertDialog open={!!statusDialog} onOpenChange={() => setStatusDialog(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {statusDialog?.status === "ACTIVE" ? t("suspendReseller") : t("activateReseller")}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {statusDialog?.status === "ACTIVE"
                ? `${t("areYouSure")} ${t("suspend")} "${statusDialog?.name}"? ${t("merchantsWillBeAffected")}`
                : `${t("areYouSure")} ${t("activate")} "${statusDialog?.name}"?`
              }
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t("cancel")}</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleToggleStatus}
              className={statusDialog?.status === "ACTIVE" ? "bg-red-600 hover:bg-red-700" : "bg-emerald-600 hover:bg-emerald-700"}
            >
              {statusDialog?.status === "ACTIVE" ? t("suspend") : t("activate")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete Dialog */}
      <AlertDialog open={!!deleteDialog} onOpenChange={() => setDeleteDialog(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t("deleteReseller")}</AlertDialogTitle>
            <AlertDialogDescription>
              {t("deleteConfirmation")} "{deleteDialog?.name}"? {t("cannotBeUndone")}
              {t("merchantsBecomeOrphaned")}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t("cancel")}</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => deleteMutation.mutate(deleteDialog.id)}
              className="bg-red-600 hover:bg-red-700"
            >
              {t("delete")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}